<template>
  <!--
    Kontener tabeli kategorii pojazdów, stylizowany według aktualnego kroku (step).
    Widoczny tylko, gdy są wybrane kategorie w store.filters.ctg8p1.
  -->
  <div class="vehicle-categories ds-panel" :class="`step-${step}`">
    <!--
      Nagłówek panelu: wyświetla przetłumaczony tekst 'vehicle-categories' wielkimi literami
      oraz informację (8+1) o liczbie kategorii.
    -->
    <div class="ds-panel__head">
      <h3 class="ds-panel__title">
        {{ t('vehicle-categories').toUpperCase() }} (8+1)
      </h3>
    </div>

    <!--
      Tabela kategorii: każda kolumna odpowiada jednej kategorii pojazdu.
      Klasa dynamiczna 'step-{step}' umożliwia różnicowanie stylu między krokami.
    -->
    <div class="ds-table-scroll">
    <table class="category-table" :class="`step-${step}`">
      <thead>
        <tr>
          <!-- Nagłówki kolumn generowane na podstawie store.vehicleCategories -->
          <th
            v-for="(item, index) in store.vehicleCategories"
            :key="'th-' + index"
          >
            <!-- Gdy nazwa kategorii to 'lack', wyświetlamy tłumaczenie klucza 'lack', w przeciwnym wypadku samą nazwę -->
            {{ item === 'lack' ? t('lack') : item }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <!-- Wiersz z checkboxami: pozwalają zaznaczyć/odznaczyć kategorie -->
          <td
            v-for="(item, index) in store.vehicleCategories"
            :key="'cb-' + index"
          >
            <input-checkbox
              :field-name="index"
              field-value=""
              :checkedCondition="isChecked(index)"
              @clicked="setFilter(index)"
            />
          </td>
        </tr>
        <!--
          Ostatni wiersz: wyświetla ikonę dla każdej kategorii.
          Dodatkowa klasa 'with-border-left' gdy jest tylko jedna lokalizacja.
        -->
        <tr class="last" :class="{ 'with-border-left': this.store.locations.length == 1 }">
          <td
            v-for="(item, index) in store.vehicleCategories"
            :key="'icon-' + index"
            :class="'icon-' + item"
          ></td>
        </tr>
      </tbody>
    </table>
    </div>
  </div>
</template>

<script>
// Import InputCheckbox: komponent reprezentujący pojedynczy checkbox
import InputCheckbox from "../InputCheckbox.vue";
// Hook tłumaczeń z vue-i18n
import { useI18n } from 'vue-i18n';

export default {
  name: "VehicleCategories",  // Nazwa komponentu dla Vue DevTools
  props: {
    // Store Pinia: przechowuje dane kategorii i obiekty filtrów
    store: { required: true },
    // Aktualny krok wyświetlania, używany do nadawania klas CSS
    step: { type: Number, default: 1 },
  },
  created() {
    // Pobranie listy lokalizacji w celu warunkowego stylowania tabeli
    this.store.getLocations();
  },
  components: { 
    InputCheckbox, // Rejestracja komponentu InputCheckbox
  },
  setup() {
    // Funkcja tłumaczenia dostępna jako this.t
    const { t } = useI18n();
    return { t };
  },
  methods: {
    /**
     * Przełącza filtr kategorii:
     * - Dodaje lub usuwa kategorię o danym indeksie w liście vehicleCategories
     * @param {number} index - pozycja kategorii w tablicy
     */
    setFilter(index) {
      this.store.addItem("VehicleCategories", index);
    },
     /**
     * Sprawdza, czy kategoria jest aktualnie zaznaczona:
     * - Wyszukuje nazwę kategorii pod indeksem w filtrach ctg8p1
     * @param {number} id - indeks kategorii
     * @returns {boolean}
     */
    isChecked(id) {
      return (
        this.store.filters.ctg8p1 &&
        this.store.filters.ctg8p1.includes(this.store.vehicleCategories[id])
      );
    },
  },
};
</script>

<style scoped>
/* Panel kategorii pojazdów: trzy wiersze — numer kategorii, wybór, piktogram.
   Bez sztywnej szerokości — panel dostosowuje się do siatki widoku. */
.vehicle-categories {
  min-width: 0;
}

.category-table {
  width: 100%;
  /* Dziewięć kolumn musi się zmieścić także w węższym oknie — poniżej tej
     szerokości panel przewija tabelę u siebie (`.ds-table-scroll`), zamiast
     rozpychać widok w poziomie. */
  min-width: 300px;
  border-collapse: collapse;
  table-layout: fixed;
  background-color: var(--surface-primary);
}

/* Numery kategorii — wyraźne, bo są głównym identyfikatorem kolumny.
   Kolumn jest dziewięć, więc padding poziomy trzymamy minimalny, aby etykieta
   „Brak" nie dotykała krawędzi panelu. */
.category-table thead th {
  padding: var(--spacing-sm) 1px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  text-align: center;
  vertical-align: middle;
}

/* Oba wiersze danych mają dokładnie tyle, co wiersz listy lokalizacji
   (`--location-row-height` = 50 px). Dzięki temu w jednej linii leżą naraz:
   krawędź między wierszem checkboxów a wierszem piktogramów — z krawędzią
   między pierwszą a drugą lokalizacją, oraz dolna krawędź panelu — z dolną
   krawędzią „Miejsca pomiaru" (2 × 50 px). */
.category-table tbody td {
  padding: var(--spacing-sm) 1px;
  height: 50px;
  font-size: var(--font-size-sm);
  text-align: center;
  vertical-align: middle;
}

/* Wiersz z piktogramami kategorii — oddzielony delikatną linią. Wysokość jak
   wiersz wyżej, patrz komentarz przy `.category-table tbody td`. */
.category-table tbody tr.last td {
  height: 50px;
  border-top: 1px solid var(--border-subtle);
}

/* `display: flex` (zamiast domyślnego `inline-flex` z design-system) wyjmuje
   kontrolkę z linii tekstu. Jako element blokowy jest centrowana przez
   `vertical-align: middle` komórki, a nie ustawiana na linii bazowej — inaczej
   podcięcie strutu podnosi ją o ~2 px względem checkboxów w „Miejscu pomiaru",
   gdzie ta sama reguła obowiązuje od początku. */
td :deep(.container) {
  display: flex;
  margin: 0 auto;
}

[class^="icon-"] {
  background-repeat: no-repeat;
  background-position: center;
}
</style>